import React from 'react';
import * as d3 from 'd3';
import { nodeSymbolType } from '../pedigree/reproSymbol';
import { twinGroupPath } from '../pedigree/path';
import { TWIN_TYPE } from '../pedigree/config';
import {
  adoptedBracketsPath,
  fertilityMarkPath,
  PROBAND_ARROW_HEAD_PATH
} from '../pedigree/nodeMarkPaths';

/**
 * The pedigree-notation icons for the context menu.
 *
 * Five of these are REAL NOTATION, and every one of them is built from the same
 * geometry the canvas draws with — `nodeSymbolType` for the person symbols,
 * `adoptedBracketsPath` / `fertilityMarkPath` / `PROBAND_ARROW_HEAD_PATH` from
 * `nodeMarkPaths`, and `twinGroupPath` for the zygosity glyphs. None of it is
 * redrawn by hand, so the menu cannot come to disagree with the chart about what
 * a thing looks like. That is the whole reason nodeMarkPaths exists.
 *
 * Everything decorative: the accessible name comes from the row's label, so every
 * svg here is aria-hidden.
 *
 * !! CHECK THESE AT 18px, NOT ZOOMED. The design notes record that the twin
 * glyphs had to be redrawn once because the first attempt was indistinguishable
 * at final size, and the Gender icon below packs three shapes into the width of
 * one.
 */

const STROKE = 1.6;
const BOX = 18;

const Svg = ({ children, width = BOX, height = BOX, viewBox }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    width={width}
    height={height}
    viewBox={viewBox || `${-width / 2} ${-height / 2} ${width} ${height}`}
    style={{ display: 'block', overflow: 'visible' }}
  >
    {children}
  </svg>
);

const stroked = (d, key) => (
  <path
    key={key}
    d={d}
    fill="none"
    stroke="currentColor"
    strokeWidth={STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
  />
);

const symbolPath = (gender, size) =>
  d3
    .symbol()
    .type(nodeSymbolType({ gender }))
    .size(size * size)();

/**
 * Gender — the square and the circle.
 *
 * This started as three shapes (D6): the submenu offers eleven identity options,
 * `genderMappings` collapses them to square / circle / diamond, and six land on
 * the diamond, so drawing only two implied a binary the setting does not have.
 *
 * **Reversed after browser QA (Tim, round 2).** Three symbols in an icon gutter
 * cannot be both spaced and legible — even at a pitch that technically cleared
 * them they read as squashed. Two shapes at a size you can actually see beats
 * three you cannot, and the diamond is not lost information: every option in the
 * submenu now carries the symbol IT would draw, which is a better place to learn
 * that "Indeterminate" is a diamond than a 32px composite.
 *
 * Sizing rule, since this was got wrong once: d3's `.size()` is an AREA. A square
 * of area s² is √(s²) = s across, but a circle of the same area is 2·√(s²/π) ≈
 * 1.13·s across — so the pitch has to clear the CIRCLE, not the square.
 *
 * TWO gaps have to survive, and the second is easy to lose: one BETWEEN the
 * square and the circle, and one AFTER the circle before the label. The row's
 * gutter is a fixed `ICON_SLOT` with the glyph left-aligned in it, so that second
 * gap is whatever the icon leaves unused — an icon drawn to the full slot width
 * ends up touching the label. Hence 25 into a 32px slot rather than 30.
 */
const GENDER_SYMBOL_SIZE = 10;
const GENDER_PITCH = 13;

export const GenderIcon = () => (
  <Svg width={25} viewBox="-12.5 -9 25 18">
    {[
      { gender: 'male', x: -GENDER_PITCH / 2 },
      { gender: 'female', x: GENDER_PITCH / 2 }
    ].map(({ gender, x }) => (
      <g key={gender} transform={`translate(${x}, 0)`}>
        {stroked(symbolPath(gender, GENDER_SYMBOL_SIZE))}
      </g>
    ))}
  </Svg>
);

/** A single person symbol, for the rows that add one. */
export const PersonIcon = ({ gender = 'unknown' }) => (
  <Svg>{stroked(symbolPath(gender, 12))}</Svg>
);

/** The mating notation — square and circle joined, as the sidebar's Spouse tile. */
export const SpouseIcon = () => (
  <Svg width={24} viewBox="-12 -9 24 18">
    {stroked('M -5 0 H 5')}
    <g transform="translate(-7, 0)">{stroked(symbolPath('male', 9))}</g>
    <g transform="translate(7, 0)">{stroked(symbolPath('female', 9))}</g>
  </Svg>
);

/**
 * Adopted — Bennett square brackets around the symbol, from the same
 * `adoptedBracketsPath` the canvas strokes.
 */
export const AdoptedIcon = () => (
  <Svg viewBox="-9 -9 18 18">
    {stroked(adoptedBracketsPath(9), 'brackets')}
    {stroked(symbolPath('male', 8), 'symbol')}
  </Svg>
);

/**
 * Fertility — one crossbar for "No Issue", two for infertile.
 *
 * The parent ROW uses the default (the double bar): the submenu behind it offers
 * both marks, so either would be arbitrary, and the double bar is the one the
 * design specified and the more distinctive at this size. There it stands for the
 * group rather than for the current value. Inside the submenu each option passes
 * its own `value`, so a row shows the mark it would actually draw.
 *
 * Returns null for an unset value: "None" means no mark, and an empty gutter says
 * that better than a struck-through glyph would.
 *
 * The mark is drawn BELOW a symbol on the canvas (y from 0.52·S to 1.05·S), so it
 * is translated up here to sit in the middle of the icon box.
 */
export const FertilityIcon = ({ value = 'infertile' }) => {
  const size = 12;
  const top = size * 0.52;
  const bottom = value === 'infertile' ? size * 1.05 : size * 0.85;
  const path = fertilityMarkPath(value, size);
  if (!path) return null;
  return (
    <Svg viewBox="-9 -9 18 18">
      <g transform={`translate(0, ${-(top + bottom) / 2})`}>{stroked(path)}</g>
    </Svg>
  );
};

/**
 * Make Proband — the proband arrow, entering from the lower left at the canvas's
 * own -45°, with the marker triangle from `PROBAND_ARROW_HEAD_PATH`.
 *
 * The chart's 200-unit shaft is meaningless in an 18px box (see nodeMarkPaths),
 * so only the head and the angle are shared — the two things anyone would notice
 * drifting.
 */
const PROBAND_SHAFT_START = -11;
const PROBAND_SHAFT_END = -2;
// The marker path is 12 × 12 with its tip at (12, 6).
const PROBAND_HEAD_SCALE = 0.7;
const PROBAND_HEAD_SIZE = 12 * PROBAND_HEAD_SCALE;

// Rotating about the origin maps a box's centre to the rotated centre, and the
// rotated box's bounding box is symmetric about that point — so this is exactly
// the offset that puts the arrow back in the middle of the icon. Derived rather
// than typed in, so it stays right if the shaft or the head is resized again.
const PROBAND_BOX_CENTRE_X = (PROBAND_SHAFT_START + PROBAND_SHAFT_END) / 2;
const COS45 = Math.SQRT1_2;
const PROBAND_RECENTRE_X = -(PROBAND_BOX_CENTRE_X * COS45);
const PROBAND_RECENTRE_Y = -(-PROBAND_BOX_CENTRE_X * COS45);

export const ProbandIcon = () => (
  <Svg viewBox="-9 -9 18 18">
    {/* The chart draws this arrow INTO the lower-left corner of a symbol, so
        rotating it alone leaves it hugging the bottom-left of the icon box —
        browser QA found it barely visible down there, then asked for it bigger
        again. Read right-to-left: rotate first, then move the result back to
        the middle. */}
    <g
      transform={`translate(${PROBAND_RECENTRE_X}, ${PROBAND_RECENTRE_Y}) rotate(-45)`}
    >
      {stroked(`M ${PROBAND_SHAFT_START} 0 H ${PROBAND_SHAFT_END}`)}
      <g
        transform={`translate(${PROBAND_SHAFT_END - PROBAND_HEAD_SIZE}, ${
          -PROBAND_HEAD_SIZE / 2
        }) scale(${PROBAND_HEAD_SCALE})`}
      >
        <path d={`${PROBAND_ARROW_HEAD_PATH} Z`} fill="currentColor" />
      </g>
    </g>
  </Svg>
);

/**
 * The zygosity glyphs, driven through the real `twinGroupPath` with synthetic
 * coordinates: a union above, two twins below, and the twinRelations that make
 * the pair "confirmed". Monozygotic gains the horizontal bar between the
 * diagonals, dizygotic does not, and unknown gets the "?" in the opening.
 */
const twinIconPath = (type) => {
  const union = { x: 0, y: -7 };
  const twins = [
    {
      pid: 1,
      x: -6,
      y: 7,
      twinRelations: [{ withPid: 2, type }]
    },
    {
      pid: 2,
      x: 6,
      y: 7,
      twinRelations: [{ withPid: 1, type }]
    }
  ];
  return twinGroupPath(union, twins);
};

export const TwinIcon = ({ type = TWIN_TYPE.dizygotic }) => {
  const { path, unknownMarkers } = twinIconPath(type);
  return (
    <Svg viewBox="-9 -9 18 18">
      {stroked(path)}
      {unknownMarkers.map((m, i) => (
        <text
          key={i}
          x={m.x}
          y={m.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={7}
          fontWeight={700}
          fill="currentColor"
        >
          ?
        </text>
      ))}
    </Svg>
  );
};

/** "Not a twin" — one plain descent line, which is what a singleton draws. */
export const SingletonIcon = () => (
  <Svg viewBox="-9 -9 18 18">
    {stroked('M 0 -7 V 2')}
    {stroked(symbolPath('male', 7))}
  </Svg>
);

/**
 * Reproductive — the Bennett diamond with its interior 'P', identical to the
 * sidebar's pregnancy launcher. The donor glyph uses the same shape with a 'D';
 * the diamond is deliberate for both (a donor's drawn shape is derived from the
 * gamete type, which is not chosen until the form opens).
 */
export const ReproIcon = ({ letter = 'P' }) => (
  <Svg viewBox="-9 -9 18 18">
    {stroked(symbolPath('unknown', 14))}
    <text
      x={0}
      y={0}
      textAnchor="middle"
      dominantBaseline="central"
      // A diamond's inscribed area is much smaller than its width, so the letter
      // is a smaller fraction of the symbol than it would be inside a square.
      fontSize={6}
      fontWeight={700}
      fill="currentColor"
    >
      {letter}
    </text>
  </Svg>
);
